/**
 * For view influencers stats and comparing them against another.
 */

import { getCache } from '@/utils/cache';
import { get, post, postSme } from '@/utils/request';
import { cloneDeep } from 'lodash';
import { defineStore } from 'pinia';
import { useConfsStore } from './confs';
import { useCampaignStore } from './campaign';

export interface ISearchInfluencersForCampaign {
  age_from: number;
  age_to: number;
  age_group?: number[]; // not part of the api call
  channels_with_levels: { channel: string; levels: string[] }[];
  content_creator_type_ids: string[];
  country?: string; // not part of the api call
  free_text: string;
  influencer_group_ids: string[];
  influencer_levels: string[];
  income_categories: string[];
  locations: string[];
  page_number: number;
  page_size: number;
  platforms?: string[];
  sortings: any[];
  gender: string;
  occupations: string[] | undefined;
  subcategories: any[];
}

export const emptySearchForm: ISearchInfluencersForCampaign = {
  age_from: 18,
  age_to: 100,
  age_group: [18, 100],
  channels_with_levels: [],
  content_creator_type_ids: [],
  country: '',
  free_text: '',
  influencer_group_ids: [],
  influencer_levels: [],
  income_categories: [],
  locations: [],
  page_number: 1,
  page_size: 25,
  sortings: [],
  platforms: [],
  gender: 'BOTH',
  occupations: [],
  subcategories: [],
};

export const useInfluencersStore = defineStore('influencers', {
  state: () => ({
    profile: getCache('profile'),
    influencerData: null as any,
    error: null as any,
    influencerSearchForm: cloneDeep(emptySearchForm) as ISearchInfluencersForCampaign,
    influencersFromSearch: [] as any[],
    influencersFromSearchMetadata: null as any,
    influencersSearchIsLoading: false,
    selectedInfluencers: [] as any[],
    unSelectedInfluencers: [] as any[],
  }),
  actions: {
    async doGetInfluencerDetails(influencer_id: string) {
      try {
        const influencerDetails = await get(`/influencer/social-media-data/${influencer_id}`);
        this.influencerData = influencerDetails;
        return influencerDetails;
      } catch (e) {
        this.error = e;
      }
    },

    //* Influencer search -------------------------------------------------------------------
    async doSearchInfluencers(options?: { updateSelectedInfluencers?: boolean }) {
      try {
        this.influencersSearchIsLoading = true;
        const tmp = Object.assign({ ...this.influencerSearchForm }) as ISearchInfluencersForCampaign;
        const channels_with_levels = [];
        if (tmp?.platforms?.length) {
          for (const channel of tmp?.platforms) {
            channels_with_levels.push({
              channel,
              levels:
                tmp.influencer_levels.length === 0
                  ? (useConfsStore().influencerLevels.map(e => e.value) as string[])
                  : tmp.influencer_levels,
            });
          }
        }

        if (this.influencerSearchForm.locations.length === 0) {
          tmp.locations = useConfsStore()
            .getCities(this.influencerSearchForm.country || '')
            .map(e => e.value);
        }

        tmp.age_from = tmp?.age_group?.[0] || 18;
        tmp.age_to = tmp?.age_group?.[1] || 100;
        (tmp.subcategories = [
          ...useCampaignStore().campaignForm.subcategories.map(e => ({ enum_value: e, subcategory_type: 'POSTING' })),
          ...(this.influencerSearchForm.occupations?.map(e => ({ enum_value: e, subcategory_type: 'OCCUPATION' })) || []),
        ]),
          delete tmp.platforms;
        delete tmp.age_group;

        const res = await post('/influencers/search_for_campaign', { ...tmp, channels_with_levels });
        this.influencersFromSearch = res.influencers_for_search ?? [];
        this.influencersFromSearchMetadata = res;

        // update selected influencer data
        if (options?.updateSelectedInfluencers) {
          this.selectedInfluencers = this.selectedInfluencers.map((oldInfluencerData: any) => {
            const newInfluencerData = this.influencersFromSearch.find((inf: any) => inf.id === oldInfluencerData.id);
            if (newInfluencerData) {
              return {
                ...newInfluencerData,
                matching_channels: Array.from(new Set([...newInfluencerData.matching_channels, ...oldInfluencerData.matching_channels])),
                matching_channels_with_levels: Array.from(
                  new Set([...newInfluencerData.matching_channels_with_levels, ...oldInfluencerData.matching_channels_with_levels]),
                ),
              };
            }
            return oldInfluencerData;
          });
        }

        return res;
      } finally {
        this.influencersSearchIsLoading = false;
      }
    },
    async doSelectInfluencersByIds(ids: string[], useSme?: boolean) {
      this.error = null;

      try {
        const page_size = 50;
        const data = [];
        // recursive fetch
        for (let i = 0; i < Math.ceil(ids.length / page_size); i++) {
          const requestFn = useSme ? postSme : post;

          const res = await requestFn('/influencers/find_by_ids', {
            ids,
            page_size,
            page_number: i + 1,
          });
          data.push(...res.influencers);
        }

        return data.map(influencer => {
          return {
            ...influencer,
            matching_channels: influencer.channels,
            matching_channels_with_levels: influencer.channels_levels,
          };
        });
      } catch (e) {
        this.error = e;
        throw e;
      }
    },

    reset() {
      this.influencersFromSearch = [];
      this.influencerData = null;
      this.selectedInfluencers = [];
      this.influencerSearchForm = cloneDeep(emptySearchForm) as ISearchInfluencersForCampaign;
    },
  },

  getters: {
    selectedInfluencersChannels(state) {
      return Array.from(new Set([].concat(...state.selectedInfluencers.map(e => e.matching_channels))));
    },
  },
});
